"use client"

import { Textarea } from "@/components/ui/textarea"
import { useState, useMemo, useEffect, useRef, useCallback } from "react"
import { useSearchParams } from "next/navigation"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Building2,
  Eye,
  EyeOff,
  ChevronsUpDown,
  Loader2,
  Check,
  UserCircle,
  FileCheck2,
} from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import { useCurrentTenantQuery } from "@/hooks/queries/current-tenant"
import { usePermissions } from "@/hooks/use-permissions"
import { type User } from "@/hooks/queries/users"
import { useUpdateUserMutation } from "@/hooks/mutations/users"
import type React from "react"
import { cn } from "@/lib/utils"
import {
  buildTimezoneOptions,
  withCustomTimezone,
  type TimezoneOption,
} from "@/lib/utils/timezones"
import { OrganizationTab } from "@/components/organization/organization-tab"
import { AiDisclosureReport } from "@/components/organization/ai-disclosure-report"
import { TENANTS_PERMISSIONS } from "@/lib/auth/permissions.constants"

type ProfileTab = "profile" | "organization" | "compliance-reports"

export function UserProfilePage() {
  const { user, refreshAuth } = useAuth()
  const { tenant } = useCurrentTenantQuery()
  const { can } = usePermissions()
  const updateUserMutation = useUpdateUserMutation()
  const searchParams = useSearchParams()

  // Organization settings and reports require tenants.view_own, including deep links.
  const canViewOrg = can(TENANTS_PERMISSIONS.VIEW_OWN)

  const tabParam = searchParams.get("tab")
  const initialTab = resolveSettingsTab(tabParam, canViewOrg)
  const [activeTab, setActiveTab] = useState<ProfileTab>(initialTab)
  const visibleTab = resolveSettingsTab(activeTab, canViewOrg)

  const handleTabChange = useCallback((value: string) => {
    const tab = resolveSettingsTab(value, canViewOrg)
    setActiveTab(tab)
    if (typeof window === "undefined") return
    const url = new URL(window.location.href)
    if (tab === "profile") {
      url.searchParams.delete("tab")
    } else {
      url.searchParams.set("tab", tab)
    }
    window.history.replaceState({}, "", url.toString())
  }, [canViewOrg])

  const [formData, setFormData] = useState({
    name: "",
    password: "",
    confirmPassword: "",
    phone: "",
    website: "",
    description: "",
    timezone: "UTC",
  })

  // Load user data into form only on initial mount (not after refreshAuth)
  const formInitialized = useRef(false)
  useEffect(() => {
    if (user && !formInitialized.current) {
      formInitialized.current = true
      setFormData({
        name: user.name || "",
        password: "",
        confirmPassword: "",
        phone: String(user.phone || ""),
        website: String(user.website || ""),
        description: String(user.description || ""),
        timezone: user.timezone || "UTC",
      })
    }
  }, [user])

  const baseTimezoneOptions = useMemo<TimezoneOption[]>(() => buildTimezoneOptions(), [])
  const timezoneOptions = useMemo(
    () => withCustomTimezone(baseTimezoneOptions, formData.timezone),
    [baseTimezoneOptions, formData.timezone],
  )
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false)
  const [timezoneSearch, setTimezoneSearch] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const filteredTimezoneOptions = useMemo(() => {
    const query = timezoneSearch.trim().toLowerCase()
    if (!query) {
      return timezoneOptions
    }
    return timezoneOptions.filter((option) => option.searchText.includes(query))
  }, [timezoneOptions, timezoneSearch])

  const selectedTimezoneOption = useMemo(
    () => timezoneOptions.find((option) => option.value === formData.timezone),
    [timezoneOptions, formData.timezone],
  )

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus(null)

    if (formData.password && formData.password !== formData.confirmPassword) {
      setStatus({ type: "error", message: "Passwords do not match." })
      return
    }

    if (formData.password && formData.password.length > 0 && formData.password.length < 6) {
      setStatus({ type: "error", message: "Password must be at least 6 characters long." })
      return
    }

    if (!user) return

    setIsSubmitting(true)
    try {
      const updateData: Partial<User> & { password?: string } = {
        name: formData.name,
        phone: formData.phone || undefined,
        website: formData.website || undefined,
        description: formData.description || undefined,
        timezone: formData.timezone,
      }

      if (formData.password) {
        updateData.password = formData.password
      }

      await updateUserMutation.mutateAsync({ uuid: user.uuid ?? user.user_id, userData: updateData })
      // Refresh JWT to pick up updated name/timezone claims
      await refreshAuth()

      setStatus({ type: "success", message: "Profile updated successfully!" })
      // Keep current form values (phone/website/description are not in JWT),
      // only clear password fields
      setFormData((prev) => ({ ...prev, password: "", confirmPassword: "" }))
    } catch (error: unknown) {
      setStatus({ type: "error", message: error instanceof Error ? error.message : "Failed to update profile." })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!user) {
    return (
      <div className="p-6 max-w-4xl mx-auto flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <p>Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Tabs value={visibleTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="min-h-10 h-auto flex flex-wrap justify-start [&>button]:min-h-10">
          <TabsTrigger value="profile">
            <UserCircle className="w-4 h-4 mr-2" />
            <span>Profile</span>
          </TabsTrigger>
          {canViewOrg && (
            <TabsTrigger value="organization">
              <Building2 className="w-4 h-4 mr-2" />
              <span>Organization</span>
            </TabsTrigger>
          )}
          {canViewOrg && (
            <TabsTrigger value="compliance-reports">
              <FileCheck2 className="w-4 h-4 mr-2" />
              <span>Compliance Reports</span>
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="profile" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Profile</CardTitle>
          <CardDescription>Update your personal information here.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateProfile} className="space-y-6">
            {status && (
              <Alert
                className={status.type === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}
              >
                <AlertDescription className={status.type === "success" ? "text-green-800" : "text-red-800"}>
                  {status.message}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoComplete="name"
                />
              </div>
              <div>
                <Label htmlFor="email">Account Email</Label>
                <Input id="email" type="email" value={user.email} disabled autoComplete="email" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Leave blank to keep current"
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  autoComplete="tel"
                />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                  autoComplete="url"
                />
              </div>
            </div>

            <div className="max-w-xl">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="timezone">Time Zone *</Label>
                  {tenant?.timezone && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto px-2 py-1 text-xs"
                      disabled={tenant.timezone === formData.timezone}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          timezone: tenant.timezone ?? prev.timezone,
                        }))
                      }
                      title="Copy organization timezone into your profile"
                    >
                      Use organization timezone
                    </Button>
                  )}
                </div>
                <Popover open={isTimezoneOpen} onOpenChange={(open) => setIsTimezoneOpen(open)}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      id="timezone"
                      aria-haspopup="listbox"
                      aria-expanded={isTimezoneOpen}
                      className={cn(
                        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                        !selectedTimezoneOption && "text-muted-foreground",
                      )}
                    >
                      <span className="line-clamp-1">
                        {selectedTimezoneOption ? selectedTimezoneOption.label : "Select your time zone"}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
                    <Command shouldFilter={false}>
                      <div className="border-b border-border bg-popover">
                        <CommandInput
                          autoFocus
                          value={timezoneSearch}
                          onValueChange={setTimezoneSearch}
                          placeholder="Search time zone..."
                        />
                      </div>
                      <CommandList className="max-h-64 overflow-y-auto">
                        {filteredTimezoneOptions.length > 0 ? (
                          <CommandGroup className="p-0">
                            {filteredTimezoneOptions.map((option) => (
                              <CommandItem
                                key={option.value}
                                value={option.value}
                                onSelect={() => {
                                  setFormData((prev) => ({ ...prev, timezone: option.value }))
                                  setTimezoneSearch("")
                                  setIsTimezoneOpen(false)
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    option.value === formData.timezone ? "opacity-100" : "opacity-0",
                                  )}
                                />
                                <span className="line-clamp-2">{option.label}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        ) : (
                          <div className="px-3 py-4 text-sm text-muted-foreground">No matching time zones</div>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  Seasonal clock changes are applied automatically for the
                  selected time zone.
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                placeholder="Tell us a little about yourself"
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <span key={isSubmitting ? "saving" : "idle"}>
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </span>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
        </TabsContent>
        {canViewOrg && (
          <TabsContent value="compliance-reports" className="space-y-6">
            <AiDisclosureReport />
          </TabsContent>
        )}
        {canViewOrg && (
          <TabsContent value="organization" className="space-y-6">
            <OrganizationTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

function resolveSettingsTab(value: string | null, canViewOrganization: boolean): ProfileTab {
  switch (value) {
    case "organization":
    case "compliance-reports":
      return canViewOrganization ? value : "profile"
    default:
      return "profile"
  }
}
